import React, { Dispatch, SetStateAction, useContext, useState } from "react";
import {
  Box,
  Column,
  Columns,
  Dialog,
  Hidden,
  Stack,
  TextField,
  Text,
  Button,
  Section,
} from "@origin-digital/ods-core";
import { IconSearch } from "@origin-digital/ods-icons";
import ClaimState from "./ClaimState";
import RequestingCompany from "../FormComponents/RequestingCompany";
import {
  Field,
  FieldMetaProps,
  FieldProps,
  Formik,
  FormikProps,
  yupToFormErrors,
} from "formik";
import {
  ClaimableStates,
  claimDialogInitialValues,
  ClaimDialogValues,
  ComputedClaimableJourney,
} from "./types";
import ProjectName from "../FormComponents/ProjectName";
import { useNavigate } from "react-router-dom";
import { apiClient } from "src/api/apiService";
import { determineClaimable } from "./helper";
import { AppContext } from "src/context/AppContext";
import {
  claimDialogValidationSchema,
  claimSearchRequestValidationSchema,
} from "./validations";
import { AxiosError } from "axios";

interface IClaimDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const ClaimDialog = (props: IClaimDialogProps) => {
  const { open, setOpen } = props;
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentClaimTarget, setCurrentClaimTarget] =
    useState<ComputedClaimableJourney | null>();
  const [error, setError] = useState<AxiosError | null>(null);
  const {
    context: { user, requestingCompany, project },
  } = useContext(AppContext);

  const navigate = useNavigate();
  const handleClaimRequest = (values: ClaimDialogValues) => {
    navigate(`/claim`, {
      state: {
        ...values,
        currentClaimTarget,
        claimTarget: currentClaimTarget,
      },
    });
  };
  const triggerSearchEwr = async (
    ewr: string,
    currentlySelectedCompanyMsa?: string,
    currentlySelectedCompanyId?: string
  ) => {
    setLoading(true);
    try {
      setSearched(false);
      const claimableSearchResponse = await apiClient.get(
        `/claim/claimable/${ewr}`,
        {
          params: {
            requestingCompanyId: currentlySelectedCompanyId,
          },
        }
      );
      setCurrentClaimTarget(
        determineClaimable(
          claimableSearchResponse.data,
          user?.main_company?.msa_number,
          user?.id,
          currentlySelectedCompanyMsa,
          user?.role
        )
      );
      setLoading(false);
      setSearched(true);
    } catch (e) {
      if (e instanceof AxiosError) {
        setSearched(false);
        setLoading(false);
        if (e.response?.status === 404) {
          setCurrentClaimTarget({
            claimableJourney: null,
            computedState: ClaimableStates.NOT_FOUND,
          });
        } else {
          setError(e as AxiosError);
        }
        setSearched(true);
      }
    }
  };
  return (
    <Dialog
      id="dialog"
      open={open}
      title="Claim DB Request"
      maxWidth="lg"
      onClose={() => {
        setOpen(false);
        setSearched(false);
      }}
    >
      <Formik<ClaimDialogValues>
        initialValues={{
          ...claimDialogInitialValues,
          projectName: project?.name ? project.name : "",
          projectId: project?.id ? project.id : "",
        }}
        validationSchema={claimDialogValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values) => handleClaimRequest(values)}
      >
        {({
          setFieldValue,
          submitForm,
          validateForm,
          errors,
          values,
          setErrors,
        }) => (
          <Box paddingTop="small">
            <Stack space="xlarge">
              {/* Search fields */}
              <Stack
                space={{
                  xs: "large",
                  lg: "xlarge",
                }}
              >
                {/* Claiming company + add to project */}
                <Columns
                  collapseBelow="lg"
                  space={{
                    xs: "small",
                    lg: "xlarge",
                  }}
                >
                  <Column width="3/4">
                    <Columns
                      collapseBelow="md"
                      space={{
                        xs: "xlarge",
                        md: "medium",
                      }}
                    >
                      <RequestingCompany
                        error={errors["requestingCompany"]}
                        setFieldValue={setFieldValue}
                        submitForm={submitForm}
                        validateForm={validateForm}
                        isEditing={false}
                        requestingCompany={
                          requestingCompany
                            ? requestingCompany
                            : values.requestingCompany
                        }
                      />
                      <Column>
                        <ProjectName
                          setFieldValue={setFieldValue}
                          submitForm={submitForm}
                          validateForm={validateForm}
                          requestingCompany={values.requestingCompany}
                          projectName={values.projectName}
                          projectId={values.projectId}
                        />
                      </Column>
                    </Columns>
                  </Column>
                  <Column width="1/3" />
                </Columns>
                {/* End Claiming company + add to project */}

                {/* Request IDs */}
                <Columns
                  collapseBelow="lg"
                  space={{
                    xs: "none",
                    lg: "xlarge",
                  }}
                  alignY="stretch"
                >
                  {/* Fields */}
                  <Column width="3/4">
                    <Field id="requestId" name="requestId">
                      {({
                        field,
                        meta,
                      }: {
                        field: FormikProps<FieldProps>;
                        meta: FieldMetaProps<ClaimDialogValues>;
                      }) => (
                        <TextField
                          {...field}
                          id="requestId"
                          name="requestId"
                          label="Distributor's Request ID"
                          variant="outlined"
                          hideHelperTextSpace={true}
                          error={!!meta.error}
                          helperText={
                            meta.error
                              ? meta.error
                              : "In VIC, the ID will start with CR or UECR. In QLD, it will start with EWR."
                          }
                        />
                      )}
                    </Field>
                  </Column>
                  {/* Fields */}
                  {/* HelperText */}
                  <Column width="1/4">
                    <Box display="flex" alignItems="stretch" height="full">
                      <Hidden below="lg">
                        {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                        <Box
                          style={{ borderLeft: "1px solid #E3E3E3" }}
                          paddingRight="large"
                          height="full"
                        />
                        {/* End Faux vertical <Divider> */}
                      </Hidden>
                      <Box alignSelf="center" marginTop="small">
                        <Stack
                          space={{
                            xs: "small",
                            md: "medium",
                          }}
                        >
                          <Text variant="body-small" tone="neutralLight">
                            This ID can be obtained from an electrician.
                          </Text>
                          {/* If more paragraphs of <Text> required, add ere */}
                        </Stack>
                      </Box>
                    </Box>
                  </Column>
                  {/* End HelperText */}
                </Columns>
                {/* End Request IDs */}

                <Button
                  icon={<IconSearch color="inherit" />}
                  fullWidth={{
                    xs: true,
                    sm: true,
                    md: false,
                    lg: false,
                    xl: false,
                  }}
                  spinner={loading}
                  onClick={() => {
                    claimSearchRequestValidationSchema
                      .validate(values, { abortEarly: false })
                      .then(() => {
                        triggerSearchEwr(
                          values.requestId,
                          values.requestingCompany?.msaNumber,
                          values.requestingCompany?.id
                        );
                      })
                      .catch((e) => {
                        setErrors(yupToFormErrors(e));
                      });
                  }}
                >
                  Search requests
                </Button>
              </Stack>
              {/* End Search fields */}
              {/* Search result */}
              {searched ? (
                <Columns
                  collapseBelow="lg"
                  space={{
                    xs: "small",
                    lg: "xlarge",
                  }}
                >
                  <Column width="3/4">
                    <Section>
                      <Stack>
                        <ClaimState
                          address={
                            currentClaimTarget?.claimableJourney
                              ?.property_details
                          }
                          claimableState={currentClaimTarget?.computedState}
                          referenceNumber={values.requestId}
                          submitForm={submitForm}
                          ocNumber={
                            currentClaimTarget?.claimableJourney
                              ?.claimed_request_oc_number
                          }
                          setOpen={setOpen}
                          error={error}
                        />
                      </Stack>
                    </Section>
                  </Column>
                  <Column width="1/3" />
                </Columns>
              ) : null}
              {/* End Search result */}
            </Stack>
          </Box>
        )}
      </Formik>
    </Dialog>
  );
};

export default ClaimDialog;
